import { Component, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import {
  FormsModule,
  ReactiveFormsModule,
  Validators,
  FormBuilder,
  FormGroup,
  FormControl,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatIconModule } from '@angular/material/icon';
import { RegisterService } from 'app/services/register.service';
import { DialogUtilsService } from 'app/modules/utils/dialog-utility.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormStateService } from 'app/services/formstate.service';
import { ConsentService } from 'app/services/consent.service';
import { FerrerAuthService } from 'app/services/authentication.service';
import { FormArray } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EmailVerifyStepComponent } from '../email-verify-window/email_verify_window.component';
import { updatePostalCodeValidators, noWhitespaceValidator } from 'app/validators/postalCodeValidator';
import { NavigationFlowService } from 'app/services/navigation-flow.service';
import { firstValueFrom } from 'rxjs';

interface CountryCode {
  value: string;      // The country code (e.g., "ES")
  label: string;      // Full label e.g. "(+34) España"
  prefix: string;     // Just the prefix e.g. "+34"
  name?: string;      // Optional: Just the country name (e.g., "España")
}

@Component({
  selector: 'app-registration-step',
  templateUrl: './registration-step.component.html',
  styleUrls: ['./registration-step.component.scss'],
  imports: [
    TranslocoPipe,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    NgSelectModule,
    CommonModule,
    MatProgressSpinnerModule
  ],
})

export class RegistrationStepComponent {
  @Input() email: string = '';
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() navigate = new EventEmitter<string>();
  anySubConsentChecked = false;
  grupoFerrerLinkHtml = "<a href='https://www.ferrer.com/es/nuestra-actividad/presencia-global' target='_blank' class='text-[#006E75] hover:text-[#003E42] hover:underline cursor-pointer'>Grupo Ferrer</a>";
  registrationForm: FormGroup;
  private resizeListener: (() => void) | null = null;
  showPassword: boolean = false;
  isSubmitting = false;
  constructor(
    private translocoService: TranslocoService,
    private _dialogRef: MatDialogRef<any>,
    private _dialog: MatDialog,
    private registerService: RegisterService,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private dialogUtilsService: DialogUtilsService,
    private formStateService: FormStateService,
    private consentService: ConsentService,
    private ferrerAuth: FerrerAuthService,
    private navigationFlow: NavigationFlowService
  ) { }
  protectionInformation: SafeHtml = '';
  termsContent: string = '';
  userPrivacyContent: string = '';
  registerApiError: string = '';
  isOpen: boolean = false;
  isPhoneFocused = false;
  countrySearchTerm: string = '';
  allCountries: any[] = [];
  filteredCountries: any[] = [];
  mainConsent: boolean = false;
  subConsent1: boolean = false;
  subConsent2: boolean = false;
  disableMain: boolean = false;
  disableSubs: boolean = false;
  shouldShowPartnersField: boolean = false;
  dialCodeSearchTerm: string = '';
  filteredDialCodes: any[] = [];
  subConsents: { contentType: string; checked: boolean; content: string }[] = [];
  countryCodes: CountryCode[] = [
    { value: '52', label: '(+52) México', prefix: '+52' },
    { value: '34', label: '(+34) España', prefix: '+34' },
  ];
  selectedCountryCode: CountryCode = this.countryCodes[0];

  ngOnInit(): void {
    try {
      // This will throw error if cross-origin
      origin = window.parent.origin;
    } catch (e) {
      // If can't access parent origin directly, use referrer
      origin = document.referrer ? new URL(document.referrer).origin : null;
    }

    this.createForm();

    // Initialize postal code validators based on any pre-selected country
    updatePostalCodeValidators(this.registrationForm, this.registrationForm.get('country')?.value || null);

    // Re-validate postal code on each change if whitespace validator is present to clear/add error live
    const postalCtrl = this.registrationForm.get('postalCode');
    postalCtrl?.valueChanges.subscribe(() => {
      if (postalCtrl?.hasValidator?.(noWhitespaceValidator)) {
        postalCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
    });

    this.registrationForm.get('country')?.valueChanges.subscribe((countryCode) => {
      this.updateConsentSpecialDataProcessingValidation();
      if (countryCode) {
        this.checkIfCountryIsInConnect(countryCode);
        updatePostalCodeValidators(this.registrationForm, countryCode);
      } else {
        // If no country is selected, hide the field and clear validators
        this.shouldShowPartnersField = false;
        this.updateConsentSpecialPartnersValidation();
        updatePostalCodeValidators(this.registrationForm, null);
      }
    });
    this.restoreSavedFormState();
    this.loadconsentsCache();
    this.loadtermsPrivacyCache();
    this.loadcountriesProfessionCache();
    this.loadCountryCodes();
    this.filteredCountries = [...this.countries];
    this.filteredDialCodes = [...this.countryCodes];

    // Set selected country in search term when available
    if (this.selectedCountry) {
      const country = this.countries.find(c => c.value === this.selectedCountry);
      if (country) {
        this.countrySearchTerm = country.label;
      }
    }

    this.loadveevainfo();
  }

  // Properties
  isProfessionalAreaOpen = false;
  professionalAreas = [];

  // Add this property to your component class
  isPhoneComponentFocused = false;

  // Add this method to your component class
  checkPhoneFocus(): void {
    // Use setTimeout to allow the focus to move to another element if user is tabbing
    setTimeout(() => {
      // Check if any element inside phone container has focus
      const phoneContainer = document.querySelector('.phone-input-container');
      const activeElement = document.activeElement;

      // Check if the active element is inside the phone container
      this.isPhoneComponentFocused = phoneContainer?.contains(activeElement) || false;
    }, 0);
  }
  // Methods
  toggleProfessionalAreaDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }

    // Toggle the dropdown state
    this.isProfessionalAreaOpen = !this.isProfessionalAreaOpen;

    // Close other dropdowns
    this.isCountryOpen = false;
    this.isOpen = false;
    if (this.isProfessionalAreaOpen) {
      this.registrationForm.get('profession')?.markAsTouched();
    }
  }

  selectProfessionalArea(value: string, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.selectedProfessionalArea = value;
    this.registrationForm.get('profession')?.setValue(value);
    this.isProfessionalAreaOpen = false;

  }

  getProfessionalAreaLabel(value: string): string {
    const option = this.professionalAreas.find(item => item.value === value);
    return option ? option.label : '';
  }

  isCountryOpen = false;
  countries = [];

  selectedProfessionalArea: string = '';
  selectedCountry: string = '';

  // Methods for country dropdown
  toggleCountryDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }

    this.isCountryOpen = !this.isCountryOpen;

    // Close other dropdowns
    this.isProfessionalAreaOpen = false;
    this.isOpen = false;
    if (this.isCountryOpen) {
      this.registrationForm.get('country')?.markAsTouched();
      setTimeout(() => this.filterCountries());
    }
  }

  // New method to just open the dropdown without toggling
  openCountryDropdown(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    // Make sure we don't toggle, just open
    if (!this.isCountryOpen) {
      this.isCountryOpen = true;
      this.registrationForm.get('country')?.markAsTouched();
      setTimeout(() => this.filterCountries());
    }

    // Close other dropdowns
    this.isProfessionalAreaOpen = false;
    this.isOpen = false;
  }

  // Normalize a string by removing accents/diacritics and converting to lowercase
  private normalizeStr(value: string): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')               // Separate letters and diacritics
      .replace(/[\u0300-\u036f]/g, '') // Remove combining marks
      .replace(/['’`´]/g, '')           // Remove apostrophe/accent variants
      .trim();
  }

  // Checks inclusion ignoring accents and case
  private includesLocale(haystack: string, needle: string): boolean {
    if (!needle) return true;
    return this.normalizeStr(haystack).includes(this.normalizeStr(needle));
  }

  // Fix the filterCountries method
  filterCountries(): void {

    if (!this.countries || this.countries.length === 0) {
      this.filteredCountries = [];
      return;
    }

    const raw = (this.countrySearchTerm || '').trim();
    if (raw === '') {
      this.filteredCountries = [...this.countries];
      return;
    }

    const termNorm = this.normalizeStr(raw);

    // Separate matches from non-matches, keeping original order for non-matches
    const matches: any[] = [];
    const rest: any[] = [];
    for (const country of this.countries) {
      if (this.includesLocale(country.label, termNorm)) {
        matches.push(country);
      } else {
        rest.push(country);
      }
    }

    // Alphabetical order (ignoring accents) of the matches
    const activeLang = (this.translocoService.getActiveLang?.() || 'es').toLowerCase();
    // Fallback chain: activeLang -> browser locale -> 'en' -> 'es'
    const locales = [activeLang, navigator.language, 'en', 'es'].filter(Boolean) as string[];
    matches.sort((a, b) => {
      const aNorm = this.normalizeStr(a.label);
      const bNorm = this.normalizeStr(b.label);

      // Prioridad 1: los que empiezan exactamente por el término
      const aStarts = aNorm.startsWith(termNorm);
      const bStarts = bNorm.startsWith(termNorm);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      // Prioridad 2: longitud (coincidencias más cortas primero si contienen exactamente el término completo)
      // (Opcional: sólo si ambos contienen el término) - se puede comentar si no se desea
      const aIndex = aNorm.indexOf(termNorm);
      const bIndex = bNorm.indexOf(termNorm);
      if (aIndex !== bIndex) return aIndex - bIndex; // antes las coincidencias más adelantadas

      // Prioridad 3: orden alfabético locale-aware ignorando acentos
      for (const loc of locales) {
        const cmp = aNorm.localeCompare(bNorm, loc, { sensitivity: 'base' });
        if (cmp !== 0) return cmp;
      }
      return 0;
    });

    // Concatenar: primero coincidencias ordenadas, luego el resto tal cual
    this.filteredCountries = [...matches, ...rest];
    this.isCountryOpen = true; // mantener abierto mientras se escribe
  }

  // Update selectCountry method
  selectCountry(value: string, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }

    this.selectedCountry = value;
    this.updateSubconsents(this.selectedCountry);
    // Find the selected country
    const selectedCountryObj = this.countries.find(item => item.value === value);

    // Update form control value
    this.registrationForm.get('country')?.setValue(value);

    // Store original value if needed
    if (selectedCountryObj?.originalValue) {
      this.registrationForm.get('countryOriginalValue')?.setValue(selectedCountryObj.originalValue);
    }

    // Update search term to show selected country
    if (selectedCountryObj) {
      this.countrySearchTerm = selectedCountryObj.label;
      this.setCountryDialCode(selectedCountryObj);
    }

    updatePostalCodeValidators(this.registrationForm, this.selectedCountry);

    // Close the dropdown
    this.isCountryOpen = false;
  }

  // Helper used in template to know if postalCode currently has required validator
  isPostalRequired(): boolean {
    const ctrl = this.registrationForm?.get?.('postalCode');
    return !!ctrl?.hasValidator?.(Validators.required);
  }

  setCountryDialCode(country: any): void {
    if (!country || !country.value) {
      return;
    }

    // Get the country code from the selected country
    const countryCode = country.value;

    try {
      // Get mapping data from localStorage
      const countriesData = localStorage.getItem('ferrer_countries');
      const prefixesData = localStorage.getItem('ferrer_phone_prefixes');

      if (!countriesData || !prefixesData) {
        return;
      }

      // Parse the stored data
      const countries = JSON.parse(countriesData);
      const prefixes = JSON.parse(prefixesData);

      // Find the full country object with all its data
      const fullCountryData = countries.find(c => c.value === countryCode);
      if (fullCountryData && fullCountryData.dialCode) {
        // If the country object has a direct phoneCode property, use it

        // Find matching dial code in our prefixes list
        let matchingDialCode = prefixes.find(p =>
          p.value === fullCountryData.dialCode ||
          p.prefix === `+${fullCountryData.dialCode}` ||
          p.prefix === fullCountryData.dialCode
        );
        if (matchingDialCode) {
          this.selectedCountryCode = matchingDialCode;
          this.dialCodeSearchTerm = matchingDialCode.prefix;
          return;
        }
      }

      // Try to match by ISO code (common in many APIs)
      if (fullCountryData && fullCountryData.iso2) {
        const isoCode = fullCountryData.iso2;
        const matchingDialCode = prefixes.find(p => p.value === isoCode);

        if (matchingDialCode) {
          this.selectedCountryCode = matchingDialCode;
          this.dialCodeSearchTerm = matchingDialCode.prefix;
          return;
        }
      }

      // If we get here, we couldn't find a match

    } catch (error) {
    }
  }

  getCountryLabel(value: string): string {
    const option = this.countries.find(item => item.value === value);
    return option ? option.label : '';
  }

  changeLang(lang: string) {
    this.translocoService.setActiveLang(lang);
  }

  createForm(): void {
    this.registrationForm = this.fb.group({
      firstName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]+$/)]],
      lastName: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(/^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]+$/)]],
      password: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(16),
        RegistrationStepComponent.noWhitespaceValidator
      ]],
      profession: ['', [Validators.required, RegistrationStepComponent.noWhitespaceValidator]],
      country: ['', [Validators.required, RegistrationStepComponent.noWhitespaceValidator]],
      cedula: ['', [
        Validators.pattern(/^\d+$/),
        RegistrationStepComponent.noWhitespaceValidator
      ]],
      postalCode: [''],
      // redundant validators. 
      // Pattern will be dynamically applied per country via updatePostalCodeValidators
      // Validators.required,
      // RegistrationStepComponent.noWhitespaceValidator
      telephone: ['', [Validators.required, Validators.minLength(9),
      Validators.maxLength(11),
      Validators.pattern(/^\d+$/),
      RegistrationStepComponent.noWhitespaceValidator
      ]],
      mainConsent: [false],
      termsConsent: [false, [Validators.requiredTrue]],
      dataConsent: [false],
      ConsentSpecialPartners: [false],
      ConsentSpecialDataProcessing: [false],
      subConsents: this.fb.array([])
    });

    this.updateCedulaValidation();
  }
  continue() {
    this.next.emit();
  }
  get subConsentsFormArray(): FormArray {
    return this.registrationForm.get('subConsents') as FormArray;
  }

  // Call this when loading consents
  setupSubConsentControls(consents: any[], savedState: any[] = []): void {
    // Clear existing controls

    while (this.subConsentsFormArray.length) {
      this.subConsentsFormArray.removeAt(0);
    }

    // Add new controls for each consent
    consents.forEach(consent => {
      const matchingSavedConsent = savedState.find(sc => sc.contentType === consent.id);

      this.subConsentsFormArray.push(this.fb.group({
        contentType: [consent.id],
        checked: [matchingSavedConsent ? matchingSavedConsent.checked : false],
        content: [consent.detailedDescription]
      }));
    });
  }
  goBack() {
    this.back.emit();
  }
  closeDialog(): void {
    this.dialogUtilsService.closeDialog(this._dialogRef);
  }

  goToTermsStep(): void {
    this.formStateService.saveRegistrationFormState({
      formValues: this.registrationForm.value,
      countrySearchTerm: this.countrySearchTerm,
      selectedCountry: this.selectedCountry,
      dialCodeSearchTerm: this.dialCodeSearchTerm,
      selectedProfessionalArea: this.selectedProfessionalArea,
      selectedCountryCode: this.selectedCountryCode
    });
    this.navigate.emit('terms');

  }
  gotoUserPrivacyStep(): void {
    this.formStateService.saveRegistrationFormState({
      formValues: this.registrationForm.value,
      countrySearchTerm: this.countrySearchTerm,
      selectedCountry: this.selectedCountry,
      dialCodeSearchTerm: this.dialCodeSearchTerm,
      selectedProfessionalArea: this.selectedProfessionalArea,
      selectedCountryCode: this.selectedCountryCode
    });
    this.navigate.emit('privacy');

  }

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }

    this.isOpen = !this.isOpen;

    // Close other dropdowns
    this.isProfessionalAreaOpen = false;
    this.isCountryOpen = false;
    if (this.isOpen) {
      this.filterDialCodes();
    }
  }

  filterDialCodes(): void {
    if (!this.dialCodeSearchTerm || this.dialCodeSearchTerm.trim() === '') {
      this.filteredDialCodes = [...this.countryCodes];
      return;
    }

    const searchTerm = this.dialCodeSearchTerm.toLowerCase().trim();
    this.filteredDialCodes = this.countryCodes.filter(code =>
      code.label.toLowerCase().includes(searchTerm) ||
      code.prefix.toString().includes(searchTerm)
    );

    // Ensure dropdown stays open when filtering
    this.isOpen = true;
  }
  selectCode(code: CountryCode, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    this.selectedCountryCode = code;

    // Update search term to show selected code
    this.dialCodeSearchTerm = code.prefix;

    // Close the dropdown
    this.isOpen = false;
  }
  openDialCodeDropdown(event: MouseEvent): void {
    event.stopPropagation();

    // Make sure we don't toggle, just open
    if (!this.isOpen) {
      this.isOpen = true;
    }

    // Close other dropdowns
    this.isProfessionalAreaOpen = false;
    this.isCountryOpen = false;
  }
  closeDropdown() {
    this.isOpen = false;
  }
  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Don't close dropdown when clicking inside the country dropdown input field
    const isInsideCountryInput = target.tagName === 'INPUT' &&
      target.closest('.country-dropdown');
    const isInsideDialCodeInput = target.tagName === 'INPUT' &&
      target.closest('.dropdown-wrapper');


    if (isInsideCountryInput || isInsideDialCodeInput) {
      // Allow clicking in the input without closing dropdown
      return;
    }

    // Don't close if clicking inside dropdowns
    if (!target.closest('.professional-area-dropdown') &&
      !target.closest('.country-dropdown') &&
      !target.closest('.dropdown-wrapper')) {
      if (this.isCountryOpen) {
        // When the country dropdown is about to be closed,
        // update its validation status to show any errors immediately.
        this.registrationForm.get('country')?.updateValueAndValidity();
      }
      // Close all dropdowns
      this.isProfessionalAreaOpen = false;
      this.isCountryOpen = false;
      this.isOpen = false;
    }
  }
  
  async openRegisterDialog(): Promise<void> {
    // Use NavigationFlowService to transition from register to login dialog
    // This will close the current register dialog and open login dialog
    await firstValueFrom(this.navigationFlow.transitionToDialog('login'));
  }


  openEmailVerifiedDialog(): void {
    if (this._dialogRef) {
      // Close without animation
      this._dialogRef.close();
    }

    const { width, height } = this.dialogUtilsService.calculateDialogSize();

    this._dialogRef = this._dialog.open(EmailVerifyStepComponent, {
      width: `${width}px`,
      //height: `${height}px`,
      maxWidth: '90vw',
      maxHeight: '65vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      // Disable enter animation
      enterAnimationDuration: '0ms',
      // Disable exit animation
      exitAnimationDuration: '0ms',
      data: {
        email: this.email
      }
    });

    // Set up responsive behavior and get cleanup function
    const cleanupResizeListener = this.dialogUtilsService.setupResponsiveDialog(this._dialogRef);

    this._dialogRef.afterClosed().subscribe(() => {
      // Clean up the event listener
      cleanupResizeListener();
      this._dialogRef = null;
    });
  }
  onSubmit(): void {
    this.registerApiError = '';
    if (this.registrationForm.invalid) {
      // Mark all fields as touched to trigger validation messages
      Object.keys(this.registrationForm.controls).forEach(key => {
        const control = this.registrationForm.get(key);
        control?.markAsTouched();
      });
      this.registerApiError = this.translocoService.translate('errors.registerFormMissing');
      return;
    }
    this.isSubmitting = true;
    // Submit form if valid
    const consentsDict = this.subConsentsFormArray.controls.reduce((dict, control) => {
      // Extract the contentType (ID) and checked status from each form control
      const contentType = control.get('contentType').value;
      const checked = control.get('checked').value;

      // Add to dictionary with contentType as key and checked as value
      dict[contentType] = checked;
      return dict;
    }, {} as Record<string, boolean>);

    const formData = {
      ...this.registrationForm.value,
      email: this.email,
      econsents: consentsDict
    };
    const professionValue = formData.profession;
    if (professionValue && professionValue.toLowerCase() === 'otherps') {
      formData.profession = '0';
    } else if (professionValue && !professionValue.startsWith('TYP.')) {
      formData.profession = `TYP.${professionValue}`;
    }
    formData.telephone = this.selectedCountryCode.prefix + this.registrationForm.value.telephone;

    const professionalCedula = formData.cedula;
    if (professionalCedula && 
      (formData.country === 'PE' || formData.country === 'MX') 
      && professionalCedula.length < 6) {
      formData.cedula = professionalCedula.padStart(6, '0');
    }

    this.registerService.sendRegisterData(formData)
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          if (response) {
            // variara segun el contenido
            this.openEmailVerifiedDialog();
          } else {
            this.isSubmitting = false;
            this.handleApiError(response);
            this.openEmailVerifiedDialog();
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.handleApiError(err);
        }
      });
  }



  // Add this method to toggle password visibility
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  updateSubconsents(country: string) {
    this.registrationForm.get('mainConsent')?.setValue(false);
    this.consentService.getConsent(country).subscribe({
      next: (consentContent) => {
        this.subConsents = consentContent.consents.map((item: any) => ({
          contentType: item.Id,
          checked: false,
          content: item.detailedDescription
        }));

        const savedSubConsents = this.registrationForm.value.subConsents || [];
        this.setupSubConsentControls(consentContent.consents, savedSubConsents);
        this.initializeConsentState();
      },
      error: (error) => {
        this.clearSubconsents();
        this.initializeConsentState();
      }
    });
  }

  private clearSubconsents(): void {
    // Reset subConsents array to empty
    this.subConsents = [];

    // Clear all FormArray controls
    while (this.subConsentsFormArray.length) {
      this.subConsentsFormArray.removeAt(0);
    }

    // Make sure main consent is not disabled
    this.disableMain = false;
    this.disableSubs = false;
  }

  loadconsentsCache() {
    const cachedConsent = localStorage.getItem('ferrer_consentcache');
    const cachedTimestamp = localStorage.getItem('ferrer_consentcache_timestamp');

    const now = new Date().getTime();
    const expirationTime = 24 * 60 * 60 * 1000;

    const countryToUse = this.selectedCountry || this.getCurrentCountry();
    this.consentService.getConsent(countryToUse).subscribe((consentContent) => {
      if (consentContent != undefined) {
        localStorage.setItem('ferrer_consentcache_timestamp', now.toString());
        localStorage.setItem('ferrer_consentcache', JSON.stringify(consentContent));
      }
      this.subConsents = consentContent.consents.map((item: any) => ({
        contentType: item.Id,
        checked: false,
        content: item.detailedDescription
      }));
      const savedState = this.formStateService.getRegistrationFormState();
      const savedSubConsents = savedState?.formValues?.subConsents || [];
      this.setupSubConsentControls(consentContent.consents, savedSubConsents);
      this.initializeConsentState();
    });


    // }
  }
  loadtermsPrivacyCache() {
    const cachedTerms = localStorage.getItem('ferrer_terms');
    const cachedPrivacy = localStorage.getItem('ferrer_user_privacy');
    const cachedTimestamp = localStorage.getItem('ferrer_terms_timestamp');

    const now = new Date().getTime();
    //const expirationTime = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    const expirationTime = 20000;

    this.consentService.getTermsNPrivacy(this.getCurrentSessionCountry()).subscribe((termsContent) => {
      this.termsContent = termsContent.terms;
      this.userPrivacyContent = termsContent.privacy;
      this.protectionInformation = this.sanitizer.bypassSecurityTrustHtml(termsContent.disclaimer);
      localStorage.setItem('ferrer_terms', termsContent.terms);
      localStorage.setItem('ferrer_terms_timestamp', now.toString());
      localStorage.setItem('ferrer_termprivacycache', JSON.stringify(termsContent));
      localStorage.setItem('ferrer_user_privacy', termsContent.privacy);
      localStorage.setItem('ferrer_terms_timestamp', now.toString());
      this.updateDataConsentValidation();
    });

  }

  updateDataConsentValidation(): void {
    const dataConsentControl = this.registrationForm.get('dataConsent');
    if (this.showDataConsentField()) {
      // If the field is visible, it must be checked (requiredTrue)
      dataConsentControl?.setValidators([Validators.requiredTrue]);
    } else {
      // If it's not visible, it has no validators
      dataConsentControl?.clearValidators();
    }
    // Apply the new validation rules
    dataConsentControl?.updateValueAndValidity();
  }

  updateConsentSpecialPartnersValidation(): void {
    const ConsentSpecialPartners = this.registrationForm.get('ConsentSpecialPartners');
    if (this.showSpecialPartnersField()) {
      // If the field is visible, it must be checked (requiredTrue)
      ConsentSpecialPartners?.setValidators([Validators.requiredTrue]);
    } else {
      // If it's not visible, it has no validators
      ConsentSpecialPartners?.clearValidators();
    }
    // Apply the new validation rules
    ConsentSpecialPartners?.updateValueAndValidity();
  }


  updateConsentSpecialDataProcessingValidation(): void {
    const ConsentSpecialDataProcessing = this.registrationForm.get('ConsentSpecialDataProcessing');
    if (this.showSpecialDataProcessingField()) {
      // If the field is visible, it must be checked (requiredTrue)
      ConsentSpecialDataProcessing?.setValidators([Validators.requiredTrue]);
    } else {
      // If it's not visible, it has no validators
      ConsentSpecialDataProcessing?.clearValidators();
    }
    // Apply the new validation rules
    ConsentSpecialDataProcessing?.updateValueAndValidity();
  }

  loadcountriesProfessionCache() {
    const cachedProfessionals = localStorage.getItem('ferrer_professionals');
    const cachedCountries = localStorage.getItem('ferrer_countries');
    const cachedTimestamp = localStorage.getItem('ferrer_terms_timestamp');

    const now = new Date().getTime();
    const expirationTime = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    if (cachedProfessionals && cachedTimestamp && now - parseInt(cachedTimestamp, 10) < expirationTime) {
      this.professionalAreas = JSON.parse(cachedProfessionals);
    } else {
      this.registerService.getProfessionalRols().subscribe((professionalList) => {
        this.professionalAreas = professionalList;
        localStorage.setItem('ferrer_professionals', JSON.stringify(professionalList));
        localStorage.setItem('ferrer_terms_timestamp', now.toString());
      });
    }

    const region = this.getCurrentSessionCountry();
    const setDefaultCountryFromRegion = (countryList) => {
      this.countries = countryList;

      if (this.selectedCountry && this.countries.some(c => c.value === this.selectedCountry)) {
        // Ensure the label and dial code are correctly set for the restored country
        const restoredCountry = this.countries.find(c => c.value === this.selectedCountry);
        if (restoredCountry) {
          this.countrySearchTerm = restoredCountry.label;
          setTimeout(() => this.setCountryDialCode(restoredCountry), 300);
        }
        return; // Exit the function to prevent overwriting
      }
      // Find the country that matches the region code
      const defaultCountry = this.countries.find(c =>
        c.value === region ||
        c.iso2 === region ||
        c.value.startsWith(region + '-')
      );

      if (defaultCountry) {

        // Set the selected country
        this.selectedCountry = defaultCountry.value;

        // Update form control
        this.registrationForm.get('country')?.setValue(defaultCountry.value);

        // Set the display text
        this.countrySearchTerm = defaultCountry.label;
        // Set corresponding dial code
        setTimeout(() => this.setCountryDialCode(defaultCountry), 300);
      } else {
      }
    };

    this.registerService.getCountrylist().subscribe((countryList) => {
      localStorage.setItem('ferrer_countries', JSON.stringify(countryList));
      this.allCountries = [...countryList];

      const filteredCountry = this.filterCountryList(countryList);
      this.countries = filteredCountry;
      setDefaultCountryFromRegion(this.countries);
      //this.filterCountries();
    });

  }
  filterCountryList(countryList: any[]): any[] {
    // Filter out countries that are not supported
    this.allCountries = [...countryList];

    const session = JSON.parse(localStorage.getItem('ferrerSessionData'));
    //only keep the country thats in the session

    if (session.flagCountry && session.flagProduct === 1) {

      // keep only session.country
      const returnret = countryList.filter(country =>
        country.value === session.country ||
        country.iso2 === session.country ||
        (country.originalValue && country.originalValue === session.country)
      );

      return returnret;
    } else if (session.flagProduct === 1) {
      const keepList = session.countriesInRegion;

      const returnret = countryList.filter(country =>
        keepList.some(code =>
          country.value === code ||
          country.iso2 === code ||
          (country.originalValue && country.originalValue === code)
        )
      );
      return returnret;
      //return filtered list
    }

    return countryList;
  }
  loadCountryCodes(): void {
    const cachedPhonePrefixes = localStorage.getItem('ferrer_phone_prefixes');
    const cachedTimestamp = localStorage.getItem('ferrer_terms_timestamp');

    const now = new Date().getTime();
    const expirationTime = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    if (cachedPhonePrefixes && cachedTimestamp && now - parseInt(cachedTimestamp, 10) < expirationTime) {
      this.countryCodes = JSON.parse(cachedPhonePrefixes);
      this.selectedCountryCode = this.countryCodes.find(code => this.getCountryDefaultLabelBylanguge(this.translocoService.getActiveLang().toUpperCase())) || this.countryCodes[0];
    } else {
      this.registerService.getphonePrefix().subscribe({
        next: (phonePrefixes) => {
          this.countryCodes = phonePrefixes;

          this.selectedCountryCode = this.countryCodes.find(code => this.getCountryDefaultLabelBylanguge(this.translocoService.getActiveLang().toUpperCase())) || this.countryCodes[0];
          // Cache the data
          localStorage.setItem('ferrer_phone_prefixes', JSON.stringify(phonePrefixes));
          localStorage.setItem('ferrer_terms_timestamp', now.toString());
        },
        error: (error) => {
        }
      });
    }
  }
  handleApiError(error: any): void {
    // Default error message
    let errorMessage = 'An unexpected error occurred';

    // Get error response body
    const errorBody = error.error.message || error.error || error;
    this.registerApiError = errorBody;
    // Check for specific error types and map to form fields
    if (errorBody.code === 'AUTH_INVALID_CREDENTIALS' || errorBody.includes('No hay ning')) {
      // Invalid credentials - could be either email or password
      //this.emailApiError = errorBody;
    } else if (errorBody.code === 'AUTH_USER_NOT_FOUND' || errorBody.includes('user not found')) {
      // User not found - email issue
      //this.emailApiError = 'User not found';
    } else if (errorBody.code === 'AUTH_ACCOUNT_LOCKED' || errorBody.includes('locked')) {
      // Account locked
      //this.emailApiError = 'Account is locked. Please contact support.';
    } else if (errorBody.fieldErrors) {
      // Field-specific errors from API
      errorBody.fieldErrors.forEach(fieldError => {
        if (fieldError.field === 'email') {
          //    this.emailApiError = fieldError.message;
        } else if (fieldError.field === 'password') {
          //   this.passwordApiError = fieldError.message;
        }
      });
    } else {
    }
  }

  restoreSavedFormState(): void {
    const savedState = this.formStateService.getRegistrationFormState();

    if (savedState) {
      // Restore form values
      this.registrationForm.patchValue(savedState.formValues);

      // Restore dropdown state
      this.countrySearchTerm = savedState.countrySearchTerm || '';
      this.selectedCountry = savedState.selectedCountry || '';
      this.selectedProfessionalArea = savedState.selectedProfessionalArea || '';
      this.dialCodeSearchTerm = savedState.dialCodeSearchTerm || '';
      // Restore phone code
      if (savedState.selectedCountryCode) {
        this.selectedCountryCode = savedState.selectedCountryCode;
      }

      // Re-run any necessary filtering
      if (this.selectedCountry && this.countries?.length) {
        this.filterCountries();
      }
      if (savedState) {
        // After patching values, initialize consent state
        setTimeout(() => this.initializeConsentState(), 0);
      }

    }
  }
  onMainConsentChange(): void {
    // Use form value consistently instead of class property
    const mainConsentValue = this.registrationForm.get('mainConsent').value;

    if (mainConsentValue) {
      // Check all sub-consents in the FormArray
      this.subConsentsFormArray.controls.forEach(control => {
        control.get('checked').setValue(true);
      });
      this.disableSubs = true;
      this.disableMain = false;
    } else {
      // Uncheck all subconsents when main is unchecked
      this.subConsentsFormArray.controls.forEach(control => {
        control.get('checked').setValue(false);
      });
      this.disableSubs = false;
    }
  }

  updateCedulaValidation(): void {
    const cedulaControl = this.registrationForm.get('cedula');

    // Find the country object to get its code/value
    const hasCedula = JSON.parse(localStorage.getItem('currentCountryInfo')).hasUciData;

    // Check if the selected country requires cedula
    if (hasCedula) {
      // Add required validator
      cedulaControl?.setValidators([
        Validators.required,
        Validators.pattern(/^\d+$/),
        RegistrationStepComponent.noWhitespaceValidator
      ]);
    } else {
      // Remove required validator, keep pattern validation
      cedulaControl?.setValidators([
        Validators.pattern(/^\d+$/),
        RegistrationStepComponent.noWhitespaceValidator
      ]);
    }

    // Update the control with the new validators
    cedulaControl?.updateValueAndValidity();

  }
  onSubConsentChange(): void {
    // Check if any subConsent is checked
    this.anySubConsentChecked = this.subConsentsFormArray.controls.some(
      control => control.get('checked')?.value === true
    );

    if (this.anySubConsentChecked) {
      this.registrationForm.get('mainConsent')?.setValue(false);
      this.disableMain = true;
      this.disableSubs = false;
    } else {
      this.disableMain = false;
    }
  }
  getCurrentCountry(): string {
    const session = JSON.parse(localStorage.getItem('ferrerSessionData'));
    if (session) {
      return session.country;
    }
    return '';
  }

  getCurrentSessionCountry(): string {
    const session = JSON.parse(localStorage.getItem('ferrerSessionData'));
    if (session) {
      return session.country;
    }
    return '';
  }

  showCedulaField(): boolean {
    try {
      const countryInfo = localStorage.getItem('currentCountryInfo');
      if (countryInfo) {
        const parsedInfo = JSON.parse(countryInfo);
        return !!parsedInfo.hasUciData;
      }
    } catch (error) {
      console.error('Error checking if cedula field should be shown:', error);
    }

    // Default: don't show if we can't determine
    return false;
  }
  // Add this after setupSubConsentControls in loadconsentsCache()
  initializeConsentState(): void {
    // Check if main consent is selected
    const mainConsentChecked = this.registrationForm.get('mainConsent')?.value;

    // Check if any subconsent is checked
    this.anySubConsentChecked = this.subConsentsFormArray.controls.some(
      control => control.get('checked')?.value === true
    );

    // Set up proper disabled states
    if (mainConsentChecked) {
      this.disableSubs = true;
      this.disableMain = false;
    } else if (this.anySubConsentChecked) {
      this.disableMain = true;
      this.disableSubs = false;
    }
  }

  getCountryDefaultLabelBylanguge(lang: string): string {
    if (lang === 'LAT') {
      return 'MX';
    } else if (lang === 'EN') {
      return 'GB';
    } else {
      return 'ES';
    }
  }

  static noWhitespaceValidator(control: FormControl): { [key: string]: boolean } | null {
    // Check if the input string has any spaces
    const containsSpaces = (control.value || '').includes(' ');
    return containsSpaces ? { 'containsSpaces': true } : null;
  }

  showDataConsentField(): boolean {
    try {
      const termPrivacyCache = localStorage.getItem('ferrer_termprivacycache');
      if (!termPrivacyCache) return false;

      const isHca = JSON.parse(termPrivacyCache).isHca;
      return isHca === true;
    } catch (e) {
      return false; // If parsing fails, default to not showing.
    }
  }

  loadveevainfo(): void {
    this.registerService.checkUserVeevaInfo(this.email).subscribe({
      next: (veevaInfo) => {
        if (veevaInfo && Object.keys(veevaInfo).length > 0) {
          // Pre-populate form with Veeva data
          const formUpdates: any = {
            firstName: veevaInfo.firstName || '',
            lastName: veevaInfo.name || '',
            profession: veevaInfo.profession || '',
            country: veevaInfo.country || '',
            postalCode: veevaInfo.postalCode || ''
          };

          if (veevaInfo.listAddress && Array.isArray(veevaInfo.listAddress) && veevaInfo.listAddress.length > 0) {
            // First try to find primary address
            let addressWithPhone = veevaInfo.listAddress.find(addr => addr.primary && addr.phone);

            // If no primary address with phone, take any address with a phone
            if (!addressWithPhone) {
              addressWithPhone = veevaInfo.listAddress.find(addr => addr.phone);
            }

            // If we found an address with phone, process it
            if (addressWithPhone && addressWithPhone.phone) {
              const phoneNumber = addressWithPhone.phone;

              // Try to extract country code and base number
              try {
                // Handle phone formats like +559636321093
                const matchResult = phoneNumber.match(/^\+(\d+)(\d+)$/);

                if (matchResult && matchResult.length === 3) {
                  const [_, countryCode, baseNumber] = matchResult;

                  // Find the matching country code in the dropdown
                  const matchingCode = this.countryCodes.find(
                    code => code.value === countryCode || code.prefix === `+${countryCode}`
                  );

                  if (matchingCode) {
                    this.selectedCountryCode = matchingCode;
                    this.dialCodeSearchTerm = matchingCode.prefix;
                    formUpdates.telephone = baseNumber;
                  } else {
                    // If we can't find a matching country code, just use the full number
                    formUpdates.telephone = phoneNumber.replace(/\D/g, '');
                  }
                } else {
                  // If format doesn't match expected pattern, use as is
                  formUpdates.telephone = phoneNumber.replace(/\D/g, '');
                }
              } catch (e) {
                formUpdates.telephone = phoneNumber.replace(/\D/g, '');
              }
            }

            // If no postalCode was found directly, try to get it from the address
            if (!formUpdates.postalCode && addressWithPhone && addressWithPhone.provinceZip) {
              formUpdates.postalCode = addressWithPhone.provinceZip;
            }

            // If no city was found directly, try to get it from the address
            if (!formUpdates.city && addressWithPhone && addressWithPhone.city) {
              formUpdates.city = addressWithPhone.city;
            }
          }

          // Try alternate phone fields if no phone found yet
          if (!formUpdates.telephone) {
            const phoneNumber = veevaInfo.cellPhone || veevaInfo.professionalPhone;
            if (phoneNumber) {
              // Use the existing phone parsing logic
              try {
                const matchResult = phoneNumber.match(/^\+(\d+)(\d{9,})$/);

                if (matchResult && matchResult.length === 3) {
                  const [_, countryCode, baseNumber] = matchResult;

                  const matchingCode = this.countryCodes.find(
                    code => code.value === countryCode || code.prefix === `+${countryCode}`
                  );

                  if (matchingCode) {
                    this.selectedCountryCode = matchingCode;
                    this.dialCodeSearchTerm = matchingCode.prefix;
                    formUpdates.telephone = baseNumber;
                  } else {
                    formUpdates.telephone = phoneNumber.replace(/\D/g, '');
                  }
                } else {
                  formUpdates.telephone = phoneNumber.replace(/\D/g, '');
                }
              } catch (e) {
                formUpdates.telephone = phoneNumber.replace(/\D/g, '');
              }
            }
          }
          // Handle cedula/UCI if available (could be in oneKeyCode or userSpecialty)
          if (veevaInfo.oneKeyCode) {
            formUpdates.cedula = veevaInfo.oneKeyCode;
          }

          // Update the form with the Veeva data
          this.registrationForm.patchValue(formUpdates);

          if (veevaInfo.country) {
            // Find the matching country in our list
            const countryMatch = this.countries.find(
              c => c.value === veevaInfo.country ||
                c.originalValue === veevaInfo.country ||
                c.value === veevaInfo.country.toUpperCase()
            );

            if (countryMatch) {
              this.selectedCountry = countryMatch.value;
              this.countrySearchTerm = countryMatch.label;

              // Also update phone prefix based on country
              setTimeout(() => this.setCountryDialCode(countryMatch), 300);
            }
          }

          const professionValue = veevaInfo.profession || veevaInfo.userSpecialty;
          if (professionValue && this.professionalAreas.length > 0) {
            // Find the matching profession in our list using case-insensitive comparison
            const professionMatch = this.professionalAreas.find(p =>
              p.value.toLowerCase() === professionValue.toLowerCase() ||
              p.originalValue?.toLowerCase() === professionValue.toLowerCase() ||
              p.label.toLowerCase() === professionValue.toLowerCase()
            );

            if (professionMatch) {
              this.selectedProfessionalArea = professionMatch.value;
              this.registrationForm.get('profession')?.setValue(professionMatch.value);
            }
          }
          this.updateCedulaValidation();
        }
      },
      error: (error) => {
        console.error('Error loading Veeva info:', error);
      }
    });
  }

  shouldShowCloseButton(): boolean {
    const apiVersion = JSON.parse(localStorage.getItem('ferrerSessionData'));
    return this.dialogUtilsService.shouldShowCloseButton();
  }

  showSpecialDataProcessingField(): boolean {

    const selectedCountry = this.registrationForm.get('country')?.value;


    const sessionCountry = this.getCurrentSessionCountry();

    if (!selectedCountry || !sessionCountry) {
      return false;
    }

    return selectedCountry !== sessionCountry;
  }

  checkIfCountryIsInConnect(countryCode: string): void {
    // This is an asynchronous check. We call the service and update our state property.
    this.ferrerAuth.getCountryCode(countryCode).subscribe({
      next: () => {
        // HTTP 200 OK: The country IS in the connect list. HIDE the consent.
        this.shouldShowPartnersField = false;
        this.updateConsentSpecialPartnersValidation();
      },
      error: (err) => {
        if (err.status === 404) {
          // HTTP 404 Not Found: The country is NOT in the connect list. SHOW the consent.
          this.shouldShowPartnersField = true;
        } else {
          // For any other server error, default to hiding the field for safety.
          this.shouldShowPartnersField = false;
        }
        // Ensure validation is updated after the check.
        this.updateConsentSpecialPartnersValidation();
      }
    });
  }
  showSpecialPartnersField(): boolean {
    //mostrar si el el pais seleccionado no esta en la lista de country connect
    return this.shouldShowPartnersField;
  }


}
